/**
 * The return path a sign-in page was handed (#1306).
 *
 * Pages that need a session send a visitor to `/login?next=<path>` (ticket
 * checkout, bookings, the receipt page, subscription). Until #1306 nothing on
 * `/login` read it, so a member who signed in with a password or Google landed
 * on their role surface and lost the checkout. Every sign-in path now honours
 * it, through this one gate.
 *
 * Only a same-origin, path-only value is accepted: it must start with a single
 * `/`, and must not be protocol-relative (`//evil.example`), carry a scheme
 * (`https:`, `javascript:`), or smuggle a backslash (`/\evil.example`, which
 * browsers normalise to `//`). Anything else is treated as absent — the user
 * lands where they would have without the parameter. Never widen this to
 * absolute URLs: `next` is attacker-suppliable (anyone can craft a link) and an
 * open redirect off a login page is a phishing primitive.
 */
export function safeNextPath(value: string | null | undefined): string | null {
  // Callers pass an already-decoded value (`useSearchParams().get()` and the
  // App Router's `searchParams` both decode); decoding again here would
  // corrupt a legitimate `%` in a query string. The checks run on exactly the
  // string that is later navigated to.
  if (!value) return null
  if (!value.startsWith('/')) return null
  if (value.startsWith('//')) return null
  if (/[\\\u0000-\u001f]/.test(value)) return null
  return value
}

/**
 * The `/auth/callback` URL every provider redirect comes back to: Google's
 * `redirectTo`, the magic link's `emailRedirectTo` (and its Resend), with the
 * return path and the sign-up consent tick riding along as query params.
 * Host sign-ups pass `/facilitator/signup` as `next`, the shape this URL has
 * carried since #512, so the Supabase redirect allow-list is known to accept
 * the query variant. `next` must already have passed `safeNextPath`.
 */
export function authCallbackUrl(
  origin: string,
  opts: { next?: string | null; terms?: boolean } = {},
): string {
  const params = new URLSearchParams()
  if (opts.next) params.set('next', opts.next)
  if (opts.terms) params.set('terms', '1')
  const query = params.toString()
  return `${origin}/auth/callback${query ? `?${query}` : ''}`
}
